import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { 
  Mail, 
  Settings, 
  CheckCircle, 
  AlertTriangle, 
  ExternalLink,
  Copy,
  Eye,
  EyeOff,
  Shield
} from 'lucide-react';

export const AdminEmailSettingsPage = () => {
  const [ovhEmail, setOvhEmail] = useState('contact@znsolar.fr');
  const [ovhPassword, setOvhPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const handleTestEmail = async () => {
    setTesting(true);
    setTestResult(null);
    
    // Simuler un test d'email
    setTimeout(() => {
      if (ovhPassword.trim()) {
        setTestResult({ success: true, message: 'Email de test envoyé avec succès !' });
      } else {
        setTestResult({ success: false, message: 'Mot de passe OVH manquant' });
      }
      setTesting(false);
    }, 2000);
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Configuration Email</h2>
        <p className="text-gray-600 mt-1">
          Configurez OVH SMTP pour recevoir les notifications de devis par email
        </p>
      </div>

      {/* Status */}
      <Card className="border-blue-200 bg-blue-50">
        <CardContent className="pt-6">
          <div className="flex items-start space-x-3">
            <Shield className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <h3 className="font-medium text-blue-800">Configuration OVH recommandée</h3>
              <p className="text-sm text-blue-700 mt-1">
                Utilisez votre compte email OVH pour recevoir les notifications de devis gratuitement et de manière sécurisée.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* OVH Setup */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Mail className="w-5 h-5" />
            <span>Configuration OVH SMTP</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Step 1 */}
          <div className="border-l-4 border-orange-400 pl-4">
            <h4 className="font-medium text-gray-900">Étape 1 : Compte email OVH</h4>
            <p className="text-sm text-gray-600 mt-1">
              Assurez-vous d'avoir un compte email configuré chez OVH pour votre domaine znsolar.fr
            </p>
            <Button 
              variant="outline" 
              size="sm" 
              className="mt-2"
              onClick={() => window.open('https://www.ovh.com/manager/', '_blank')}
            >
              <ExternalLink className="w-4 h-4 mr-1" />
              Accéder au Manager OVH
            </Button>
          </div>

          {/* Step 2 */}
          <div className="border-l-4 border-orange-400 pl-4">
            <h4 className="font-medium text-gray-900">Étape 2 : Configuration du compte</h4>
            <p className="text-sm text-gray-600 mt-1 mb-3">
              Entrez vos identifiants email OVH :
            </p>
            
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Adresse email
                </label>
                <Input
                  type="email"
                  value={ovhEmail}
                  onChange={(e) => setOvhEmail(e.target.value)}
                  placeholder="contact@znsolar.fr"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Mot de passe email OVH
                </label>
                <div className="relative">
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={ovhPassword}
                    onChange={(e) => setOvhPassword(e.target.value)}
                    placeholder="Votre mot de passe email OVH"
                    className="pr-10"
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    className="absolute right-1 top-1 h-8 w-8 p-0"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
              
              <div className="text-xs text-gray-500">
                ⚠️ Ces informations doivent être ajoutées manuellement dans le fichier .env du serveur
              </div>
            </div>
          </div>

          {/* Server Configuration */}
          <div className="bg-gray-100 p-4 rounded-lg">
            <h4 className="font-medium text-gray-900 mb-2">Configuration serveur OVH :</h4>
            <div className="bg-black text-green-400 p-3 rounded font-mono text-sm">
              <div>SMTP_SERVER="ssl0.ovh.net"</div>
              <div>SMTP_PORT="465"</div>
              <div>SMTP_USERNAME="{ovhEmail}"</div>
              <div>SMTP_PASSWORD="{ovhPassword ? '***VOTRE_MOT_DE_PASSE***' : 'VOTRE_MOT_DE_PASSE_OVH'}"</div>
              <div>SENDER_EMAIL="{ovhEmail}"</div>
              <div>ADMIN_EMAIL="seydoumalle@znsolar.fr"</div>
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              className="mt-2"
              onClick={() => copyToClipboard(`SMTP_SERVER="ssl0.ovh.net"\nSMTP_PORT="465"\nSMTP_USERNAME="${ovhEmail}"\nSMTP_PASSWORD="${ovhPassword}"\nSENDER_EMAIL="${ovhEmail}"\nADMIN_EMAIL="seydoumalle@znsolar.fr"`)}
            >
              <Copy className="w-4 h-4 mr-1" />
              Copier la configuration
            </Button>
          </div>

          {/* Technical Info */}
          <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
            <h4 className="font-medium text-orange-800 mb-2">Paramètres techniques OVH :</h4>
            <ul className="text-sm text-orange-700 space-y-1">
              <li>• <strong>Serveur SMTP :</strong> ssl0.ovh.net</li>
              <li>• <strong>Port :</strong> 465 (SSL/TLS)</li>
              <li>• <strong>Authentification :</strong> Votre adresse email complète</li>
              <li>• <strong>Sécurité :</strong> SSL activé</li>
            </ul>
          </div>

          {/* Test Email */}
          <div className="border-t pt-4">
            <h4 className="font-medium text-gray-900 mb-2">Test d'envoi d'email</h4>
            <div className="flex space-x-2">
              <Input
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="votre@email.com"
                className="flex-1"
              />
              <Button 
                onClick={handleTestEmail}
                disabled={testing || !ovhPassword.trim() || !testEmail.trim()}
              >
                {testing ? 'Envoi...' : 'Tester'}
              </Button>
            </div>
            
            {testResult && (
              <div className={`mt-2 p-2 rounded text-sm ${
                testResult.success 
                  ? 'bg-green-50 text-green-800 border border-green-200' 
                  : 'bg-red-50 text-red-800 border border-red-200'
              }`}>
                {testResult.success ? (
                  <CheckCircle className="w-4 h-4 inline mr-1" />
                ) : (
                  <AlertTriangle className="w-4 h-4 inline mr-1" />
                )}
                {testResult.message}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Current Status */}
      <Card>
        <CardHeader>
          <CardTitle>État actuel</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Réception des devis</span>
              <Badge className="bg-green-100 text-green-800">
                <CheckCircle className="w-3 h-3 mr-1" />
                Fonctionnel
              </Badge>
            </div>
            
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Notifications email OVH</span>
              <Badge className="bg-yellow-100 text-yellow-800">
                <AlertTriangle className="w-3 h-3 mr-1" />
                Non configuré
              </Badge>
            </div>
            
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Interface admin</span>
              <Badge className="bg-green-100 text-green-800">
                <CheckCircle className="w-3 h-3 mr-1" />
                Opérationnel
              </Badge>
            </div>
          </div>
          
          <div className="mt-4 p-3 bg-orange-50 border border-orange-200 rounded">
            <p className="text-sm text-orange-800">
              💡 <strong>Avantages OVH :</strong> Configuration simple, gratuit avec votre hébergement, 
              compatible avec votre domaine znsolar.fr, et plus fiable que les services tiers.
            </p>
          </div>
          
          <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded">
            <p className="text-sm text-blue-800">
              📧 <strong>En attendant :</strong> Tous les devis sont consultables dans l'onglet "Devis" 
              de cette interface admin.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Troubleshooting */}
      <Card>
        <CardHeader>
          <CardTitle>Résolution de problèmes</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3 text-sm">
            <div>
              <strong className="text-gray-700">Erreur d'authentification :</strong>
              <p className="text-gray-600">Vérifiez que vous utilisez l'adresse email complète et le bon mot de passe.</p>
            </div>
            <div>
              <strong className="text-gray-700">Emails non reçus :</strong>
              <p className="text-gray-600">Vérifiez vos spams/courriers indésirables et que l'adresse de destination est correcte.</p>
            </div>
            <div>
              <strong className="text-gray-700">Configuration OVH :</strong>
              <p className="text-gray-600">
                Contactez le support OVH si vous avez des questions sur votre configuration email : 
                <Button variant="link" size="sm" className="p-0 h-auto" onClick={() => window.open('https://help.ovhcloud.com/', '_blank')}>
                  Documentation OVH
                </Button>
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminEmailSettingsPage;